import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import AgentCreationStep from '../enums/AgentCreationStep';
import { AgentFormData, AgentLanguage } from '../interfaces/Agent';
import { WorkflowType } from '../interfaces/App';
import { useSnackBar } from './useSnackBar';

const SESSION_KEY = 'elora_agent_form_state';

interface UseAgentFormSessionRecoveryConfig {
  methods: UseFormReturn<AgentFormData>;
  currentStepEnum: AgentCreationStep;
  workflow: WorkflowType;
  isBookingCapabilityEnabled: boolean;
}

// Persists AgentForm's Behavior/Knowledge/Capabilities-step fields to
// sessionStorage as the user edits them, and restores them on mount — so a
// calendar OAuth redirect (which reloads the page — see agentHelper.ts's
// handleConnectToCalendar) doesn't lose in-progress form data. Gated on
// isBookingCapabilityEnabled since only that flow can trigger the redirect.
// See docs/specs/agent-form-click-to-jump-navigation.md.
const useAgentFormSessionRecovery = ({
  methods,
  currentStepEnum,
  workflow,
  isBookingCapabilityEnabled,
}: UseAgentFormSessionRecoveryConfig) => {
  const displayMessage = useSnackBar();

  // Save agent capabilities step data to session storage when moving to next step OR when OAuth completes
  const saveCapabilitiesData = (data: AgentFormData) => {
    if (!isBookingCapabilityEnabled) return;

    const currentState = JSON.parse(
      sessionStorage.getItem(SESSION_KEY) || '{}'
    );
    const updatedState = {
      ...currentState,
      external_capabilities: data.external_capabilities,
      transfer_calls: data.transfer_calls || [],
      insert_appointment: data.insert_appointment || [],
      employee_directory_staged_file:
        data.employee_directory_staged_file || null,
    };

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedState));
    } catch (error) {
      console.warn(
        'Failed to save capabilities data to sessionStorage:',
        error
      );
      displayMessage(
        'Note: Unable to save form progress. Please complete the form in one session.',
        'warning',
        5000
      );
    }
  };

  // Persist the given step's booking-capability-relevant fields to
  // sessionStorage, used to recover form state after a calendar OAuth
  // redirect (which reloads the page). Callable independent of form
  // submission so any step-navigation action can invoke it, not just a
  // validated Next/Save submit.
  const persistStepDataToSessionStorage = (
    step: AgentCreationStep,
    values: AgentFormData
  ) => {
    if (!isBookingCapabilityEnabled) return;

    if (step === AgentCreationStep.BEHAVIOR) {
      const currentState = JSON.parse(
        sessionStorage.getItem(SESSION_KEY) || '{}'
      );
      const updatedState = {
        ...currentState,
        agent_name: values.agent_name,
        role_id: values.role_id,
        voice: values.voice,
        default_sentence: values.default_sentence,
        data_to_collect: values.data_to_collect,
        default_language: values.default_language,
      };

      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedState));
      } catch (error) {
        console.warn('Failed to save behavior data to sessionStorage:', error);
        displayMessage(
          'Note: Unable to save form progress. Please complete the form in one session.',
          'warning',
          5000
        );
      }
    }

    if (step === AgentCreationStep.KNOWLEDGE) {
      const currentState = JSON.parse(
        sessionStorage.getItem(SESSION_KEY) || '{}'
      );
      const updatedState = {
        ...currentState,
        knowledge_name: values.knowledge_name,
        knowledge_language: values.knowledge_language,
        knowledge_mode: values.knowledge_mode,
      };

      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedState));
      } catch (error) {
        console.warn('Failed to save knowledge data to sessionStorage:', error);
        displayMessage(
          'Note: Unable to save form progress. Please complete the form in one session.',
          'warning',
          5000
        );
      }
    }

    if (step === AgentCreationStep.CAPABILITIES) {
      saveCapabilitiesData(values);
    }
  };

  // Save capabilities data whenever the form data changes (for OAuth flow)
  useEffect(() => {
    if (currentStepEnum === AgentCreationStep.CAPABILITIES) {
      const subscription = methods.watch(data => {
        saveCapabilitiesData(data as AgentFormData);
      });
      return () => subscription.unsubscribe();
    }
  }, [currentStepEnum, methods]);

  // Save knowledge data whenever the form data changes (for OAuth flow)
  useEffect(() => {
    if (
      currentStepEnum === AgentCreationStep.KNOWLEDGE &&
      workflow === WorkflowType.CREATE &&
      isBookingCapabilityEnabled
    ) {
      const subscription = methods.watch(data => {
        if (data.knowledge_name || data.knowledge_language) {
          const currentState = JSON.parse(
            sessionStorage.getItem(SESSION_KEY) || '{}'
          );
          const updatedState = {
            ...currentState,
            knowledge_name: data.knowledge_name || '',
            knowledge_language: data.knowledge_language || 'en',
            knowledge_mode: data.knowledge_mode,
          };

          try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedState));
          } catch (error) {
            console.warn(
              'Failed to save knowledge data to sessionStorage:',
              error
            );
          }
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [currentStepEnum, workflow, methods, isBookingCapabilityEnabled]);

  // Save behavior data whenever the form data changes (for OAuth flow)
  useEffect(() => {
    if (
      currentStepEnum === AgentCreationStep.BEHAVIOR &&
      workflow === WorkflowType.CREATE &&
      isBookingCapabilityEnabled
    ) {
      const subscription = methods.watch(data => {
        if (
          data.agent_name ||
          data.role_id ||
          data.voice ||
          data.default_sentence ||
          data.data_to_collect ||
          data.default_language
        ) {
          const currentState = JSON.parse(
            sessionStorage.getItem(SESSION_KEY) || '{}'
          );
          const updatedState = {
            ...currentState,
            agent_name: data.agent_name || '',
            role_id: data.role_id || '',
            voice: data.voice || 'female',
            default_sentence: data.default_sentence || '',
            data_to_collect: data.data_to_collect || [],
            default_language: data.default_language || AgentLanguage.ENGLISH,
          };

          try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedState));
          } catch (error) {
            console.warn(
              'Failed to save behavior data to sessionStorage:',
              error
            );
          }
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [currentStepEnum, workflow, methods, isBookingCapabilityEnabled]);

  // Restore session storage data. CREATE-only: this recovers a CREATE
  // session's in-progress fields after the calendar-OAuth-redirect reload
  // (see the CREATE-only watch-subscriptions above that write this same
  // key). Without the workflow check, a blob left behind by an abandoned
  // or reloaded CREATE session in the same tab would get applied on top of
  // an unrelated agent when the EDIT form next mounts — silently
  // overwriting that agent's real, just-loaded values with stale leftovers
  // from a different agent's session.
  useEffect(() => {
    if (workflow === WorkflowType.CREATE && isBookingCapabilityEnabled) {
      const savedState = sessionStorage.getItem(SESSION_KEY);
      if (savedState) {
        try {
          const parsedData = JSON.parse(savedState);
          // Agent behavior step
          methods.setValue('agent_name', parsedData.agent_name || '');
          methods.setValue('role_id', parsedData.role_id || '');
          methods.setValue('voice', parsedData.voice || 'female');
          methods.setValue(
            'default_sentence',
            parsedData.default_sentence || ''
          );
          methods.setValue('data_to_collect', parsedData.data_to_collect || []);
          methods.setValue(
            'default_language',
            parsedData.default_language || AgentLanguage.ENGLISH
          );
          // Agent capabilities step
          methods.setValue(
            'external_capabilities',
            parsedData.external_capabilities || ''
          );
          methods.setValue('transfer_calls', parsedData.transfer_calls || []);
          methods.setValue(
            'insert_appointment',
            parsedData.insert_appointment || []
          );
          methods.setValue(
            'employee_directory_staged_file',
            parsedData.employee_directory_staged_file || null
          );
          // Agent knowledge step
          methods.setValue('knowledge_name', parsedData.knowledge_name || '');
          methods.setValue(
            'knowledge_language',
            parsedData.knowledge_language || 'en'
          );
          if (parsedData.knowledge_mode) {
            methods.setValue('knowledge_mode', parsedData.knowledge_mode);
          }
        } catch (error) {
          console.error(
            'Error parsing saved agent behavior, knowledge, and capabilities data:',
            error
          );
        }
      }
    }
  }, [workflow, methods, isBookingCapabilityEnabled]);

  return { persistStepDataToSessionStorage };
};

export default useAgentFormSessionRecovery;
